import React, { useState, useEffect, ChangeEvent, FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { UsersResponse, User } from '../types';
import './UserManagement.css';

interface CreateUserFormData {
  username: string;
  password: string;
  email: string;
}

const UserManagement: React.FC = () => {
  const navigate = useNavigate();
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [modalError, setModalError] = useState<string>('');
  const [formData, setFormData] = useState<CreateUserFormData>({
    username: '',
    password: '',
    email: ''
  });

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.get<UsersResponse>('/api/org/users');
      // Ensure users is always an array, even if API returns null, undefined, or non-array value
      const usersData = response.data?.data;
      setUsers(Array.isArray(usersData) ? usersData : []);
    } catch (err: any) {
      console.error('Error fetching users:', err);
      setError(err.response?.data?.message || err.message || 'Failed to fetch users. Please try again.');
      // Ensure users is set to empty array on error as well
      setUsers([]);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const openModal = () => {
    setIsModalOpen(true);
    setFormData({ username: '', password: '', email: '' });
    setModalError('');
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setFormData({ username: '', password: '', email: '' });
    setModalError('');
    setIsSubmitting(false);
  };

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when user starts typing
    if (modalError) {
      setModalError('');
    }
  };

  const isFormValid = formData.username.trim() !== '' && 
                      formData.email.trim() !== '' && 
                      formData.password.trim() !== '';

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);
    setModalError('');

    try {
      await api.post('/api/org/users', {
        username: formData.username,
        password: formData.password,
        email: formData.email
      });

      // Close modal and refresh users list
      closeModal();
      await fetchUsers();
    } catch (err: any) {
      console.error('Error creating user:', err);
      setModalError(err.response?.data?.message || err.message || 'Failed to create user. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const toggleUserStatus = async (userId: string, currentStatus: boolean) => {
    try {
      const newStatus = !currentStatus;
      await api.put(`/api/org/user/${userId}`, {
        is_active: newStatus
      });

      // Update the user in the local state immediately
      setUsers(prevUsers =>
        prevUsers.map(user =>
          user._id === userId
            ? { ...user, is_active: newStatus, updated_at: new Date().toISOString() }
            : user
        )
      );
    } catch (err: any) {
      console.error('Error updating user status:', err);
      setError(err.response?.data?.message || err.message || 'Failed to update user status. Please try again.');
      // Clear error after 5 seconds
      setTimeout(() => setError(null), 5000);
    }
  };

  return (
    <div className="user-management-container">
      <div className="user-management-header">
        <button className="back-button" onClick={() => navigate('/dashboard/migration')}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z" fill="currentColor"/>
          </svg>
          Back
        </button>
        <h1>Manage Users</h1>
        <button className="create-user-button" onClick={openModal}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z" fill="currentColor"/>
          </svg>
          Create User
        </button>
      </div>

      <div className="user-management-content">
        {loading ? (
          <div className="loading-container">
            <div className="loading-spinner"></div>
            <p>Loading users...</p>
          </div>
        ) : error ? (
          <div className="error-container">
            <p className="error-message">{error}</p>
            <button className="retry-button" onClick={fetchUsers}>
              Retry
            </button>
          </div>
        ) : users.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" fill="currentColor"/>
              </svg>
            </div>
            <div className="empty-text">
              <div className="empty-title">No users found</div>
              <div className="empty-subtitle">There are no users in your organization yet.</div>
            </div>
          </div>
        ) : (
          <div className="users-table-container">
            <div className="table-wrapper">
              <table className="users-table">
                <thead>
                  <tr className="table-header">
                    <th className="utable-header-cell">Username</th>
                    <th className="utable-header-cell">Email</th>
                    <th className="utable-header-cell">Role</th>
                    <th className="utable-header-cell">Status</th>
                    <th className="utable-header-cell">Created At</th>
                    <th className="utable-header-cell">Updated At</th>
                    <th className="utable-header-cell">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((user) => (
                    <tr key={user._id} className="table-row">
                      <td className="table-cell username-cell">{user.username}</td>
                      <td className="table-cell email-cell">{user.email}</td>
                      <td className="table-cell role-cell">
                        <span className={`role-badge role-${user.role.toLowerCase()}`}>
                          {user.role}
                        </span>
                      </td>
                      <td className="table-cell status-cell">
                        <span className={`status-badge ${user.is_active ? 'active' : 'inactive'}`}>
                          {user.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="table-cell date-cell">{formatDate(user.created_at)}</td>
                      <td className="table-cell date-cell">{formatDate(user.updated_at)}</td>
                      <td className="table-cell actions-cell">
                        <button
                          className="status-toggle-button"
                          onClick={() => toggleUserStatus(user._id, user.is_active)}
                          title={user.is_active ? 'Deactivate user' : 'Activate user'}
                        >
                          {user.is_active ? 'Deactivate' : 'Activate'}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {isModalOpen && (
        <div className="modal-overlay" role="dialog" aria-modal="true" onClick={(e) => {
          if (e.target === e.currentTarget) {
            closeModal();
          }
        }}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Create New User</h3>
              <button className="modal-close" type="button" onClick={closeModal} aria-label="Close modal" disabled={isSubmitting}>
                ×
              </button>
            </div>
            <form className="modal-form" onSubmit={handleSubmit}>
              <div className="modal-field">
                <label htmlFor="username-input">Username</label>
                <input
                  id="username-input"
                  name="username"
                  type="text"
                  value={formData.username}
                  onChange={handleInputChange}
                  placeholder="Enter username"
                  required
                  disabled={isSubmitting}
                />
              </div>
              <div className="modal-field">
                <label htmlFor="email-input">Email</label>
                <input
                  id="email-input"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  placeholder="Enter email"
                  required
                  disabled={isSubmitting}
                />
              </div>
              <div className="modal-field">
                <label htmlFor="password-input">Password</label>
                <input
                  id="password-input"
                  name="password"
                  type="password"
                  value={formData.password}
                  onChange={handleInputChange}
                  placeholder="Enter password"
                  required
                  disabled={isSubmitting}
                />
              </div>

              {modalError && <div className="modal-error">{modalError}</div>}

              <div className="modal-actions">
                <button
                  type="button"
                  className="modal-secondary-button"
                  onClick={closeModal}
                  disabled={isSubmitting}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="modal-primary-button"
                  disabled={isSubmitting || !isFormValid}
                >
                  {isSubmitting ? 'Creating...' : 'Create User'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserManagement;

